import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Users, BarChart3, Clock, Activity, UploadCloud, Settings } from 'lucide-react';
import { StatCard } from '../components/StatCard';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { MOCK_HISTORY, MOCK_TEAMS } from '../data/mockData';
interface DashboardProps {
  onNavigate: (page: string) => void;
}
export function Dashboard({
  onNavigate
}: DashboardProps) {
  // Calculate stats
  const totalLeads = MOCK_HISTORY.reduce((sum, h) => sum + h.totalLeads, 0);
  const activeTeams = MOCK_TEAMS.filter(t => t.active).length;
  const activeUsers = MOCK_TEAMS.reduce((sum, t) => sum + t.users.filter(u => u.active).length, 0);
  const container = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  const item = {
    hidden: {
      opacity: 0,
      y: 20
    },
    show: {
      opacity: 1,
      y: 0
    }
  };
  return <div className="p-8 max-w-7xl mx-auto space-y-8" data-id="element-93">
      <div className="flex items-center justify-between" data-id="element-94">
        <div data-id="element-95">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-96">Dashboard</h1>
          <p className="text-gray-500 mt-1" data-id="element-97">
            Mortgage lead distribution overview
          </p>
        </div>
        <div className="flex space-x-3" data-id="element-98">
          <Button variant="outline" leftIcon={<Settings className="w-4 h-4" data-id="element-100" />} onClick={() => onNavigate('teams')} data-id="element-99">
            Manage Teams
          </Button>
          <Button leftIcon={<UploadCloud className="w-4 h-4" data-id="element-102" />} onClick={() => onNavigate('upload')} data-id="element-101">
            Upload Leads
          </Button>
        </div>
      </div>

      <motion.div variants={container} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-103">
        <motion.div variants={item} data-id="element-104">
          <StatCard label="Total Leads (Mo)" value={totalLeads} change={12} trend="up" icon={BarChart3} color="indigo" data-id="element-105" />
        </motion.div>
        <motion.div variants={item} data-id="element-106">
          <StatCard label="Active Teams" value={activeTeams} icon={Users} color="blue" data-id="element-107" />
        </motion.div>
        <motion.div variants={item} data-id="element-108">
          <StatCard label="Active LOs" value={activeUsers} change={-5} trend="down" icon={Activity} color="emerald" data-id="element-109" />
        </motion.div>
        <motion.div variants={item} data-id="element-110">
          <StatCard label="Avg Dist. Time" value="1.2s" trend="neutral" icon={Clock} color="amber" data-id="element-111" />
        </motion.div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-112">
        {/* Recent Distributions */}
        <div className="lg:col-span-2 space-y-6" data-id="element-113">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-114">
            Recent Distributions
          </h2>
          <Card noPadding className="overflow-hidden" data-id="element-115">
            <div className="overflow-x-auto" data-id="element-116">
              <table className="min-w-full divide-y divide-gray-200" data-id="element-117">
                <thead className="bg-gray-50" data-id="element-118">
                  <tr data-id="element-119">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-120">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-121">
                      File Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-122">
                      Leads
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-123">
                      Status
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200" data-id="element-124">
                  {MOCK_HISTORY.slice(0, 5).map(history => <tr key={history.id} className="hover:bg-gray-50 transition-colors" data-id="element-125">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-126">
                        {new Date(history.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-127">
                        {history.fileName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-128">
                        {history.totalLeads}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap" data-id="element-129">
                        <Badge variant={history.status === 'completed' ? 'success' : history.status === 'failed' ? 'error' : history.status === 'partial' ? 'warning' : 'blue'} data-id="element-130">
                          {history.status.charAt(0).toUpperCase() + history.status.slice(1)}
                        </Badge>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200" data-id="element-131">
              <button onClick={() => onNavigate('history')} className="text-sm font-medium text-indigo-600 hover:text-indigo-500" data-id="element-132">
                View all history &rarr;
              </button>
            </div>
          </Card>
        </div>

        {/* Team Distribution Breakdown */}
        <div className="space-y-6" data-id="element-133">
          <h2 className="text-lg font-semibold text-gray-900" data-id="element-134">
            Distribution by Team
          </h2>
          <Card data-id="element-135">
            <div className="space-y-6" data-id="element-136">
              {MOCK_TEAMS.map(team => {
              const percentage = Math.round(team.totalLeadsDistributed / (totalLeads * 2) * 100); // Mock calc
              const colorMap: Record<string, string> = {
                blue: 'bg-blue-500',
                emerald: 'bg-emerald-500',
                amber: 'bg-amber-500',
                purple: 'bg-purple-500'
              };
              return <div key={team.id} data-id="element-137">
                    <div className="flex items-center justify-between mb-1" data-id="element-138">
                      <span className="text-sm font-medium text-gray-700" data-id="element-139">
                        {team.name}
                      </span>
                      <span className="text-sm text-gray-500" data-id="element-140">
                        {team.totalLeadsDistributed} leads
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-141">
                      <div className={`h-2.5 rounded-full ${colorMap[team.color]}`} style={{
                    width: `${percentage}%`
                  }} data-id="element-142"></div>
                    </div>
                  </div>;
            })}
            </div>
          </Card>
        </div>
      </div>
    </div>;
}